import React, { useState, useEffect, useCallback } from 'react';
import { GroupMapping, RoleDefinition } from '../../types';
import { getGroupMappings, saveGroupMappings, getRoles, getEntraDisplayNames } from '../../services/systemApi';

interface MappingRow {
  entraGroupId: string;
  roleName: string;
}

const GroupMappingsTab: React.FC = () => {
  const [mappings, setMappings] = useState<MappingRow[]>([]);
  const [roles, setRoles] = useState<Record<string, RoleDefinition>>({});
  const [groupDisplayNames, setGroupDisplayNames] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const roleNames = Object.keys(roles).sort();

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [mappingsResult, rolesResult] = await Promise.allSettled([
        getGroupMappings(),
        getRoles(),
      ]);
      
      if (rolesResult.status === 'fulfilled') {
        setRoles(rolesResult.value);
      } else {
        console.error('[GroupMappings] Failed to load roles:', rolesResult.reason);
      }
      
      if (mappingsResult.status === 'fulfilled') {
        const loaded = mappingsResult.value.map((m: GroupMapping) => ({
          entraGroupId: m.entraGroupId,
          roleName: m.roleName,
        }));
        setMappings(loaded);
        const ids = loaded.map(m => m.entraGroupId).filter(Boolean);
        if (ids.length > 0) {
          getEntraDisplayNames(ids)
            .then(r => setGroupDisplayNames(r.groupDisplayNames))
            .catch(e => console.warn('Could not fetch group display names:', e));
        }
      } else {
        setError(mappingsResult.reason?.message || 'Unable to load group mappings.');
      }
      setDirty(false);
    } catch (err: any) {
      setError(err.message || 'Unable to load group mappings. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const handleAddRow = () => {
    clearMessages();
    setMappings((prev) => [...prev, { entraGroupId: '', roleName: roleNames[0] || '' }]);
    setDirty(true);
  };

  const handleRemoveRow = (index: number) => {
    clearMessages();
    setMappings((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
  };

  const handleFieldChange = (index: number, field: keyof MappingRow, value: string) => {
    clearMessages();
    setMappings((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
    setDirty(true);
  };

  const handleSave = async () => {
    clearMessages();

    // Client-side validation
    for (let i = 0; i < mappings.length; i++) {
      if (!mappings[i].entraGroupId.trim()) {
        setError(`Row ${i + 1}: Entra Group ID is required.`);
        return;
      }
      if (!mappings[i].roleName) {
        setError(`Row ${i + 1}: Role is required.`);
        return;
      }
    }

    setSaving(true);
    try {
      await saveGroupMappings(
        mappings.map((m) => ({
          entraGroupId: m.entraGroupId.trim(),
          roleName: m.roleName,
        }))
      );
      setSuccess('Group mappings saved successfully.');
      setDirty(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save group mappings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-gray-500">Loading group mappings...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center justify-between" role="alert">
          <span>{error}</span>
          <button
            onClick={() => { setError(null); loadData(); }}
            className="ml-4 text-sm font-medium text-red-700 underline hover:text-red-900"
          >
            Retry
          </button>
        </div>
      )}

      {/* Success banner */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md flex items-center justify-between" role="status">
          <span>{success}</span>
          <button
            onClick={() => setSuccess(null)}
            className="ml-4 text-sm font-medium text-green-700 hover:text-green-900"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {/* Header with Add Mapping button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Entra ID Group Mappings</h3>
        <button
          type="button"
          onClick={handleAddRow}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Add Mapping
        </button>
      </div>

      {/* Mappings table */}
      {mappings.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
          No group mappings configured. Click "Add Mapping" to create one.
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Entra Group ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {mappings.map((row, index) => (
                <tr key={index}>
                  <td className="px-6 py-4">
                    <input
                      type="text"
                      value={row.entraGroupId}
                      onChange={(e) => handleFieldChange(index, 'entraGroupId', e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g. a1b2c3d4-e5f6-..."
                      aria-label={`Entra Group ID for mapping ${index + 1}`}
                    />
                    {row.entraGroupId && groupDisplayNames[row.entraGroupId] !== undefined && (
                      <p className="mt-1 text-xs text-gray-500">
                        {groupDisplayNames[row.entraGroupId] ?? <span className="italic">Name unavailable</span>}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={row.roleName}
                      onChange={(e) => handleFieldChange(index, 'roleName', e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm focus:ring-blue-500 focus:border-blue-500"
                      aria-label={`Role for mapping ${index + 1}`}
                    >
                      {roleNames.length === 0 && (
                        <option value="">No roles available</option>
                      )}
                      {roleNames.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <button
                      type="button"
                      onClick={() => handleRemoveRow(index)}
                      disabled={saving}
                      className="inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Save button */}
      {dirty && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {saving ? 'Saving...' : 'Save Mappings'}
          </button>
        </div>
      )}
    </div>
  );
};

export default GroupMappingsTab;
